/**
 * Natal 2026 — agregação e formatação da tela. SEM regra de negócio: meta, ritmo, status, estoque por
 * local e projeção vêm prontos do serviço (services/natal-2026/core/calculo.py, testado). Aqui só se
 * soma por categoria/canal/loja e se escolhe a medida (unidades de venda, faturamento, custo).
 */
import type { Anuncio, CatalogoTerceiro, Cobertura, ItemFila, NatalData, Parcela, SkuNatal, StatusRitmo, SugestaoPreco, VendaAnuncio } from './types'

export type Medida = 'un' | 'fat' | 'custo'
export type Filtro = { categoria: string; status: string; canal: string; loja: string }

export const CATEGORIAS: Record<string, string> = { arvore: 'Árvores', iluminacao: 'Iluminação', bolas: 'Kits de bolas', inflavel: 'Infláveis' }
export const STATUS: Record<StatusRitmo, string> = { ATRAS: 'Atrás', NO_RITMO: 'No ritmo', A_FRENTE: 'À frente', SEM_LEITURA: 'Sem leitura' }
export const PARCELAS: [Parcela, string][] = [
  ['galpao', 'Galpão Cabreúva'], ['full_ml_matriz', 'Full ML Matriz'], ['full_ml_extrema', 'Full ML Extrema'], ['full_shopee', 'Full Shopee Matriz'],
  ['full_shopee_decora', 'Full Shopee Decora'],
]
export const FONTE_PARCELA: Record<Parcela, string> = {
  galpao: 'Tiny · atende ML/Shopee sem Full, Shein, TikTok, Amazon',
  full_ml_matriz: 'API do ML · total no CD (inclui em transferência) · só vende neste Full',
  full_ml_extrema: 'API do ML · total no CD (inclui em transferência) · só vende neste Full',
  full_shopee: 'API da Shopee · só vende neste Full',
  full_shopee_decora: 'API da Shopee (loja Decora) · só vende neste Full',
}

const nf0 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })
export const num = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '—' : nf0.format(v))
export const brl = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '—' : `R$ ${nf0.format(v)}`)
export const pct = (v: number | null | undefined, casas = 0) =>
  v == null || !Number.isFinite(v) ? '—' : `${(v * 100).toFixed(casas).replace('.', ',')}%`
const nf2 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
/** Preço com centavos. */
export const brl2 = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '—' : `R$ ${nf2.format(v)}`)
/** Data e hora de São Paulo (dd/mm hh:mm). */
export const horaSp = (iso: string) => new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
export const dataBr = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`

/** Lojas que passam no filtro de canal/loja. */
export function lojasDoFiltro(d: NatalData, f: Filtro): string[] {
  return d.lojas.filter((l) => (f.canal === 'todos' || l.canal === f.canal) && (f.loja === 'todas' || l.store_key === f.loja)).map((l) => l.store_key)
}
export const filtroCanalAtivo = (f: Filtro) => f.canal !== 'todos' || f.loja !== 'todas'

/** Participação das lojas filtradas na meta (1 sem filtro de canal). */
export function participacao(d: NatalData, f: Filtro): number {
  if (!filtroCanalAtivo(f)) return 1
  return lojasDoFiltro(d, f).reduce((a, k) => a + (d.premissas.meta_participacao_loja[k] || 0), 0)
}

export function skusFiltrados(d: NatalData, f: Filtro): SkuNatal[] {
  return d.skus.filter((k) => (f.categoria === 'todas' || k.categoria === f.categoria) && (f.status === 'todos' || k.calc.status === f.status))
}

const somaLojas = (k: SkuNatal, lojas: string[] | null, campo: 'pecas' | 'fat' | 'mc', n: number) => {
  const out = new Array<number>(n).fill(0)
  for (const [loja, s] of Object.entries(k.vendas)) {
    if (lojas && !lojas.includes(loja)) continue
    s[campo].forEach((v, i) => (out[i] += v))
  }
  return out
}

export type Serie = { meta: number[]; real: (number | null)[]; proj: (number | null)[]; teto: number[] | null; total: number }
export type Temporada = { acum: Serie; dia: { meta: number[]; real: (number | null)[]; proj: (number | null)[] }; ritmo7: number | null }

/** Série da temporada na medida escolhida. Com filtro de canal: realizado das lojas filtradas e meta ×
 *  participação delas (premissa oficial). Projeção = meta futura × fator do SKU (vem do serviço). */
export function temporada(d: NatalData, ks: SkuNatal[], medida: Medida, f: Filtro): Temporada {
  const n = d.calendario.length, ih = d.i_hoje
  const lojas = filtroCanalAtivo(f) ? lojasDoFiltro(d, f) : null
  const share = participacao(d, f)
  const metaD = new Array<number>(n).fill(0), realD = new Array<number>(n).fill(0), projD = new Array<number>(n).fill(0), chegD = new Array<number>(n).fill(0)
  for (const k of ks) {
    const fator = medida === 'un' ? 1 / k.un_venda : medida === 'fat' ? k.preco_plan_peca : k.custo
    const pecas = somaLojas(k, lojas, 'pecas', n)
    const fat = medida === 'fat' ? somaLojas(k, lojas, 'fat', n) : null
    for (let i = 0; i < n; i++) {
      metaD[i] += k.meta_dia[i] * fator * share
      chegD[i] += k.chegada_dia[i] * (medida === 'fat' ? k.preco_plan_peca : medida === 'un' ? 1 / k.un_venda : k.custo)
      if (i <= ih) realD[i] += fat ? fat[i] : pecas[i] * fator
      else projD[i] += k.meta_dia[i] * k.calc.fator_projecao * fator * share
    }
  }
  const acum = (a: number[]) => a.reduce<number[]>((o, v, i) => (o.push((o[i - 1] || 0) + v), o), [])
  const meta = acum(metaD), realC = acum(realD), projC = acum(projD), cheg = acum(chegD)
  const real = realC.map((v, i) => (i <= ih ? v : null))
  const base = ih >= 0 ? realC[ih] : 0
  const proj = projC.map((v, i) => (i < Math.max(ih, 0) ? null : base + v - (ih >= 0 ? projC[ih] : 0)))
  const j = d.premissas.janela_ritmo_dias
  const m7 = ih >= 0 ? metaD.slice(Math.max(0, ih - j + 1), ih + 1).reduce((a, v) => a + v, 0) : 0
  const r7 = ih >= 0 ? realD.slice(Math.max(0, ih - j + 1), ih + 1).reduce((a, v) => a + v, 0) : 0
  const dia = { meta: metaD, real: realD.map((v, i) => (i <= ih ? v : null)), proj: projD.map((v, i) => (i > ih ? v : null)) }
  if (medida !== 'custo') return { acum: { meta, real, proj, teto: lojas ? null : cheg, total: meta[n - 1] || 0 }, dia, ritmo7: m7 ? r7 / m7 : null }
  // custo: estoque em mãos = o que já chegou − o que saiu (container que não chegou não conta)
  const emMaos = (a: (number | null)[]) => a.map((v, i) => (v == null ? null : Math.max(cheg[i] - v, 0)))
  return {
    acum: { meta: emMaos(meta) as number[], real: emMaos(real), proj: emMaos(proj), teto: null, total: cheg[n - 1] || 0 },
    dia, ritmo7: m7 ? r7 / m7 : null,
  }
}

export type LinhaCanal = { store_key: string; nome: string; canal: string; fat7: number; meta7: number; ritmo: number | null; status: StatusRitmo; temporada: number; metaAcum: number; mc: number; pecas7: number; participacaoReal: number; participacaoMeta: number }

/** Vendas por loja × meta da loja (meta dos SKUs × participação oficial da loja). */
export function canais(d: NatalData, ks: SkuNatal[], f: Filtro): LinhaCanal[] {
  const ih = d.i_hoje, j = d.premissas.janela_ritmo_dias, ini = Math.max(0, ih - j + 1)
  const meta7Tot = ks.reduce((a, k) => a + k.calc.meta7 * k.preco_plan_peca, 0)
  const metaAcTot = ks.reduce((a, k) => a + k.calc.meta_acum * k.preco_plan_peca, 0)
  const linhas = d.lojas.filter((l) => lojasDoFiltro(d, f).includes(l.store_key)).map((l) => {
    let fat7 = 0, temp = 0, mc = 0, pecas7 = 0
    for (const k of ks) {
      const s = k.vendas[l.store_key]
      if (!s || ih < 0) continue
      for (let i = 0; i <= ih; i++) temp += s.fat[i]
      for (let i = ini; i <= ih; i++) { fat7 += s.fat[i]; mc += s.mc[i]; pecas7 += s.pecas[i] }
    }
    const sh = d.premissas.meta_participacao_loja[l.store_key] || 0
    const meta7 = meta7Tot * sh, r = meta7 > 0 ? fat7 / meta7 : null
    const status: StatusRitmo = r == null ? 'SEM_LEITURA' : r < d.premissas.limite_sobra ? 'ATRAS' : r > d.premissas.limite_ruptura ? 'A_FRENTE' : 'NO_RITMO'
    return { store_key: l.store_key, nome: l.nome, canal: l.canal, fat7, meta7, ritmo: r, status, temporada: temp, metaAcum: metaAcTot * sh, mc: fat7 ? mc / fat7 : 0, pecas7, participacaoReal: 0, participacaoMeta: sh }
  })
  // loja que divide a meta de outra (Shopee Decora → Shopee Matriz) soma na linha dela: a meta é uma só
  const juntas = juntarMeta(d, linhas)
  const tot = juntas.reduce((a, x) => a + x.fat7, 0)
  juntas.forEach((x) => (x.participacaoReal = tot ? x.fat7 / tot : 0))
  return juntas
}

function juntarMeta(d: NatalData, linhas: LinhaCanal[]): LinhaCanal[] {
  const com = Object.fromEntries(d.lojas.filter((l) => l.meta_com).map((l) => [l.store_key, l.meta_com as string]))
  const alvo = new Map(linhas.map((x) => [x.store_key, x]))
  const out: LinhaCanal[] = []
  for (const x of linhas) {
    const dono = com[x.store_key] && alvo.get(com[x.store_key])
    if (!dono) { out.push(x); continue }
    dono.nome = `${dono.nome} + ${x.nome.replace(/^Shopee /, '')}`
    dono.fat7 += x.fat7; dono.temporada += x.temporada; dono.pecas7 += x.pecas7
    dono.mc = dono.fat7 ? (dono.mc * (dono.fat7 - x.fat7) + x.mc * x.fat7) / dono.fat7 : 0
    dono.ritmo = dono.meta7 > 0 ? dono.fat7 / dono.meta7 : null
    dono.status = dono.ritmo == null ? 'SEM_LEITURA' : dono.ritmo < d.premissas.limite_sobra ? 'ATRAS' : dono.ritmo > d.premissas.limite_ruptura ? 'A_FRENTE' : 'NO_RITMO'
  }
  return out
}

/** Linha do faturamento em níveis (Visão geral, 13/10/2026): loja → categoria → SKU → anúncio (→ pedidos, na tela).
 * Meta só até o SKU (meta do SKU × participação da loja); anúncio não tem meta. Participação = parte do faturamento
 * 7 dias da linha no nível de cima × parte da meta. */
export type NoFaturamento = {
  chave: string; nivel: 1 | 2 | 3 | 4; rotulo: string; sub?: string
  fat7: number; meta7: number | null; ritmo: number | null; status: StatusRitmo | null; temporada: number; metaAcum: number | null
  pecas7: number; mc: number | null; partReal: number | null; partMeta: number | null
  /** quanto de fat7 / temporada vem de pedidos pagos sem margem oficial ainda (MC estimada) */
  pend7: number; pendTemp: number
  filhos: NoFaturamento[]
  /** nível 4: anúncio para abrir os pedidos (anuncio "" = venda sem anúncio identificado: sem pedidos para abrir) */
  pedidos?: { loja: string; anuncio: string; sku: string } | null
  link?: string | null
}

export const ORDEM_CATEGORIA = ['arvore', 'iluminacao', 'inflavel', 'bolas']

function statusFat(d: NatalData, fat7: number, meta7: number): [number | null, StatusRitmo] {
  const r = meta7 > 0 ? fat7 / meta7 : null
  return [r, r == null ? 'SEM_LEITURA' : r < d.premissas.limite_sobra ? 'ATRAS' : r > d.premissas.limite_ruptura ? 'A_FRENTE' : 'NO_RITMO']
}

/** Faturamento em níveis. Loja que divide a meta de outra (Shopee Decora → Matriz) entra no grupo da dona, como em `canais`. */
export function faturamentoNiveis(d: NatalData, ks: SkuNatal[], f: Filtro): NoFaturamento[] {
  const ih = d.i_hoje, ini = Math.max(0, ih - d.premissas.janela_ritmo_dias + 1)
  const lojasF = lojasDoFiltro(d, f)
  const dono = (k: string) => d.lojas.find((l) => l.store_key === k)?.meta_com
  const grupos: string[][] = []
  for (const l of d.lojas.filter((x) => lojasF.includes(x.store_key))) {
    const g = dono(l.store_key) && lojasF.includes(dono(l.store_key) as string) ? grupos.find((x) => x[0] === dono(l.store_key)) : null
    if (g) g.push(l.store_key)
    else grupos.push([l.store_key])
  }
  const vendas = (k: SkuNatal, lojas: string[]) => {
    let fat7 = 0, temp = 0, mc7 = 0, pecas7 = 0
    for (const lj of lojas) {
      const s = k.vendas[lj]
      if (!s || ih < 0) continue
      for (let i = 0; i <= ih; i++) temp += s.fat[i]
      for (let i = ini; i <= ih; i++) { fat7 += s.fat[i]; mc7 += s.mc[i]; pecas7 += s.pecas[i] }
    }
    return { fat7, temp, mc7, pecas7 }
  }
  const no = (chave: string, nivel: NoFaturamento['nivel'], rotulo: string, lista: SkuNatal[], lojas: string[], sh: number, filhos: NoFaturamento[], sub?: string): NoFaturamento => {
    const v = lista.reduce((a, k) => { const x = vendas(k, lojas); return { fat7: a.fat7 + x.fat7, temp: a.temp + x.temp, mc7: a.mc7 + x.mc7, pecas7: a.pecas7 + x.pecas7 } }, { fat7: 0, temp: 0, mc7: 0, pecas7: 0 })
    const meta7 = lista.reduce((a, k) => a + k.calc.meta7 * k.preco_plan_peca, 0) * sh
    const metaAcum = lista.reduce((a, k) => a + k.calc.meta_acum * k.preco_plan_peca, 0) * sh
    const [ritmo, status] = statusFat(d, v.fat7, meta7)
    const nomes = new Set(lista.map((k) => k.sku))
    const pend = (d.vendas_anuncio ?? []).filter((x) => nomes.has(x.sku) && lojas.includes(x.loja))
    return { chave, nivel, rotulo, sub, fat7: v.fat7, meta7, ritmo, status, temporada: v.temp, metaAcum, pecas7: v.pecas7, mc: v.fat7 ? v.mc7 / v.fat7 : null, partReal: null, partMeta: null,
      pend7: pend.reduce((a, x) => a + (x.fat_pend7 ?? 0), 0), pendTemp: pend.reduce((a, x) => a + (x.fat_pend_temp ?? 0), 0), filhos }
  }
  const partes = (pai: NoFaturamento) => {
    for (const x of pai.filhos) {
      x.partReal = pai.fat7 ? x.fat7 / pai.fat7 : null
      x.partMeta = x.meta7 != null && pai.meta7 ? x.meta7 / pai.meta7 : null
      partes(x)
    }
  }
  const out = grupos.map((lojas) => {
    const sh = d.premissas.meta_participacao_loja[lojas[0]] || 0
    const cats = ORDEM_CATEGORIA.filter((c) => ks.some((k) => k.categoria === c)).map((c) => {
      const kc = ks.filter((k) => k.categoria === c)
      const skus = kc.map((k) => no(`${lojas[0]}|${c}|${k.sku}`, 3, k.sku, [k], lojas, sh, anunciosDoSku(d, k.sku, lojas, `${lojas[0]}|${c}|${k.sku}`), k.descricao))
        .sort((a, b) => b.temporada - a.temporada || (b.meta7 ?? 0) - (a.meta7 ?? 0))
      return no(`${lojas[0]}|${c}`, 2, CATEGORIAS[c] ?? c, kc, lojas, sh, skus)
    })
    const nome = lojas.map((k, i) => (i ? nomeLoja(d, k).replace(/^Shopee /, '') : nomeLoja(d, k))).join(' + ')
    return no(lojas[0], 1, nome, ks, lojas, sh, cats)
  })
  const tot = out.reduce((a, x) => a + x.fat7, 0), metaTot = out.reduce((a, x) => a + (x.meta7 ?? 0), 0)
  for (const x of out) { x.partReal = tot ? x.fat7 / tot : null; x.partMeta = metaTot ? (x.meta7 ?? 0) / metaTot : null; partes(x) }
  return out
}

/** Nível 4: anúncios do SKU nas lojas do grupo — os que venderam (parte do SKU) e os ativos sem venda. */
function anunciosDoSku(d: NatalData, sku: string, lojas: string[], pai: string): NoFaturamento[] {
  const vend: VendaAnuncio[] = (d.vendas_anuncio ?? []).filter((v) => v.sku === sku && lojas.includes(v.loja))
  const acha = (loja: string, ad: string) => {
    const cands = d.anuncios.filter((a) => a.loja === loja && (a.item_id ?? a.id) === ad)
    return cands.find((a) => a.comps.some(([s]) => s === sku)) ?? cands[0] ?? null
  }
  const linha = (loja: string, ad: string, a: Anuncio | null, v: VendaAnuncio | null): NoFaturamento => ({
    chave: `${pai}|${loja}|${ad || '-'}`, nivel: 4,
    rotulo: ad ? (a?.titulo || v?.titulo || `Anúncio ${ad}`) : 'Venda sem anúncio identificado',
    sub: ad ? [nomeLoja(d, loja), ad, a?.variacao, a?.full ? 'Full' : null, a && a.status !== 'ativo' ? a.status : null].filter(Boolean).join(' · ') : nomeLoja(d, loja),
    fat7: v?.fat7 ?? 0, meta7: null, ritmo: null, status: null, temporada: v?.fat_temp ?? 0, metaAcum: null,
    pecas7: v?.pecas7 ?? 0, mc: v && v.fat7 ? v.mc7 / v.fat7 : null, partReal: null, partMeta: null, pend7: v?.fat_pend7 ?? 0, pendTemp: v?.fat_pend_temp ?? 0, filhos: [],
    pedidos: ad ? { loja, anuncio: ad, sku } : null, link: a?.link ?? null,
  })
  const vistos = new Set(vend.map((v) => `${v.loja}|${v.anuncio}`))
  const comVenda = vend.map((v) => linha(v.loja, v.anuncio, v.anuncio ? acha(v.loja, v.anuncio) : null, v))
  const semVenda = d.anuncios
    .filter((a) => lojas.includes(a.loja) && a.status === 'ativo' && a.comps.some(([s]) => s === sku) && !vistos.has(`${a.loja}|${a.item_id ?? a.id}`))
    .filter((a, i, arr) => arr.findIndex((x) => x.loja === a.loja && (x.item_id ?? x.id) === (a.item_id ?? a.id)) === i)
    .map((a) => linha(a.loja, a.item_id ?? a.id, a, null))
  return [...comVenda.sort((a, b) => b.temporada - a.temporada || b.fat7 - a.fat7), ...semVenda]
}

export type LinhaLocal = { parcela: Parcela; nome: string; estoque: number; aChegar: number; custo: number; v7: number; cobertura: number | null; sobra: number; sobraCusto: number; skusSobra: string[] }

/** Estoque por local (segregado) e sobra projetada — soma do que o serviço calculou por SKU. */
export function locais(ks: SkuNatal[]): LinhaLocal[] {
  return PARCELAS.map(([p, nome]) => {
    let estoque = 0, aChegar = 0, custo = 0, v7 = 0, sobra = 0, sobraCusto = 0
    const skusSobra: string[] = []
    for (const k of ks) {
      const x = k.calc.parcelas[p]
      const hoje = k.estoque[p] ?? 0
      if (p === 'galpao') aChegar += k.calc.a_chegar
      estoque += hoje; custo += hoje * k.custo; v7 += x.v7; sobra += x.sobra; sobraCusto += x.sobra * k.custo
      if (p !== 'galpao' && x.estoque > 0 && x.sobra > Math.max(0.15 * x.estoque, 5)) skusSobra.push(`${k.sku} (${num(x.sobra)})`)
    }
    return { parcela: p, nome, estoque, aChegar, custo, v7, cobertura: v7 ? estoque / (v7 / 7) : null, sobra, sobraCusto, skusSobra }
  })
}

export const sobraSku = (k: SkuNatal) => PARCELAS.reduce((a, [p]) => a + k.calc.parcelas[p].sobra, 0)
export const estoqueSku = (k: SkuNatal) => PARCELAS.reduce((a, [p]) => a + (k.estoque[p] || 0), 0)

export type LinhaRisco = {
  k: SkuNatal; grupo: string; parado: boolean; sobra: number; sobraCusto: number; risco: number; riscoCusto: number
  paradoCusto: number; otimistaCusto: number; estoque: number
}
export type GrupoRisco = { nome: string; paradoCusto: number; sobraCusto: number; riscoCusto: number; total: number; skus: string[] }

/** Seção "Onde está o risco" em três camadas (tiro curto, 26/10/2026): parado sem venda (SKU que passou da carência
 *  sem vender; todo o estoque projetado), sobra projetada (base) e risco adicional (pessimista − base). */
export function ondeRisco(ks: SkuNatal[]) {
  const linhas: LinhaRisco[] = ks.map((k) => {
    const base = sobraSku(k), parado = !!k.calc.parado
    const pess = Math.max(k.calc.sobra_pessimista ?? base, base), otim = Math.min(k.calc.sobra_otimista ?? base, base)
    return {
      k, grupo: k.grupo ?? k.sku, parado, estoque: estoqueSku(k),
      sobra: parado ? 0 : base, sobraCusto: parado ? 0 : base * k.custo,
      risco: parado ? 0 : pess - base, riscoCusto: parado ? 0 : (pess - base) * k.custo,
      paradoCusto: parado ? base * k.custo : 0, otimistaCusto: parado ? 0 : otim * k.custo,
    }
  }).filter((l) => l.paradoCusto + l.sobraCusto + l.riscoCusto > 0)
  const mapa = new Map<string, GrupoRisco>()
  for (const l of linhas) {
    const g = mapa.get(l.grupo) ?? { nome: l.grupo, paradoCusto: 0, sobraCusto: 0, riscoCusto: 0, total: 0, skus: [] }
    g.paradoCusto += l.paradoCusto; g.sobraCusto += l.sobraCusto; g.riscoCusto += l.riscoCusto
    g.total += l.paradoCusto + l.sobraCusto + l.riscoCusto; g.skus.push(l.k.sku)
    mapa.set(l.grupo, g)
  }
  const grupos = [...mapa.values()].sort((a, b) => b.total - a.total)
  const soma = (f: (l: LinhaRisco) => number) => linhas.reduce((a, l) => a + f(l), 0)
  const sobra = soma((l) => l.sobraCusto), risco = soma((l) => l.riscoCusto), parado = soma((l) => l.paradoCusto)
  const estoqueCusto = ks.reduce((a, k) => a + (estoqueSku(k) + k.calc.a_chegar) * k.custo, 0)
  const top4 = grupos.slice().sort((a, b) => b.sobraCusto - a.sobraCusto).slice(0, 4).reduce((a, g) => a + g.sobraCusto, 0)
  return {
    linhas: linhas.sort((a, b) => b.paradoCusto + b.sobraCusto + b.riscoCusto - a.paradoCusto - a.sobraCusto - a.riscoCusto),
    grupos, parado, sobra, risco, otimista: soma((l) => l.otimistaCusto), pessimista: sobra + risco, estoqueCusto, top4,
    skusParados: linhas.filter((l) => l.parado).length,
  }
}

/** SKUs da categoria do filtro. Status, canal e loja NÃO entram: meta é plano da temporada, não leitura do dia
 *  (decisão da Mariana em 23/10/2026 — antes o bloco de metas era fixo no total, mockup de 04/10). */
export const skusDaCategoria = (d: NatalData, f?: Filtro) =>
  (!f || f.categoria === 'todas') ? d.skus : d.skus.filter((k) => k.categoria === f.categoria)

/** Metas oficiais na categoria do filtro (Todas = temporada inteira). `porCategoria` é sempre das 4 categorias,
 *  para a tabela comparativa; `chave` é a categoria técnica para a tela destacar a linha escolhida. */
export function metasOficiais(d: NatalData, f?: Filtro) {
  const ks = skusDaCategoria(d, f)
  return {
    unidades: ks.reduce((a, k) => a + k.qtd_meta / k.un_venda, 0),
    pecasCompradas: ks.reduce((a, k) => a + k.qtd_comprada, 0),
    faturamento: ks.reduce((a, k) => a + k.qtd_meta * k.preco_plan_peca, 0),
    custo: ks.reduce((a, k) => a + k.qtd_meta * k.custo, 0),
    comprado: ks.reduce((a, k) => a + k.qtd_comprada * k.custo, 0),
    porCategoria: Object.entries(CATEGORIAS).map(([c, nome]) => {
      const g = d.skus.filter((k) => k.categoria === c)
      return { chave: c, nome, skus: g.length, pecas: g.reduce((a, k) => a + k.qtd_comprada, 0), unidades: g.reduce((a, k) => a + k.qtd_meta / k.un_venda, 0), faturamento: g.reduce((a, k) => a + k.qtd_meta * k.preco_plan_peca, 0), custo: g.reduce((a, k) => a + k.qtd_meta * k.custo, 0) }
    }),
  }
}

/** Realizado da temporada até hoje na categoria do filtro (mesma régua das metas oficiais).
 * % atingido a custo (mesma régua do "Vendido × meta acumulada"): vendido × custo ÷ meta × custo. */
export function realizadoTemporada(d: NatalData, f?: Filtro) {
  const ks = skusDaCategoria(d, f), ih = d.i_hoje
  const fatSku = (k: SkuNatal) => Object.values(k.vendas).reduce((a, s) => a + s.fat.slice(0, Math.max(ih, -1) + 1).reduce((x, v) => x + v, 0), 0)
  const r = {
    unidades: ks.reduce((a, k) => a + k.calc.vendido / k.un_venda, 0),
    unidadesMetaHoje: ks.reduce((a, k) => a + k.calc.meta_acum / k.un_venda, 0),
    faturamento: ks.reduce((a, k) => a + fatSku(k), 0),
    faturamentoMetaHoje: ks.reduce((a, k) => a + k.calc.meta_acum * k.preco_plan_peca, 0),
    custo: ks.reduce((a, k) => a + k.calc.vendido * k.custo, 0),
    custoMetaHoje: ks.reduce((a, k) => a + k.calc.meta_acum * k.custo, 0),
    custoMetaTotal: ks.reduce((a, k) => a + k.qtd_meta * k.custo, 0),
  }
  return { ...r, pctHoje: r.custoMetaHoje ? r.custo / r.custoMetaHoje : null, pctTotal: r.custoMetaTotal ? r.custo / r.custoMetaTotal : null }
}

/** Ordenação genérica para as tabelas (todas as colunas clicáveis). */
export function ordenar<T>(linhas: T[], valor: (l: T) => number | string | null, dir: 'asc' | 'desc'): T[] {
  return [...linhas].sort((a, b) => {
    const va = valor(a), vb = valor(b)
    const r = typeof va === 'number' && typeof vb === 'number' ? va - vb : va == null ? 1 : vb == null ? -1 : String(va).localeCompare(String(vb), 'pt-BR')
    return dir === 'asc' ? r : -r
  })
}

// ====================================================================== Fases 2 a 4 (só filtro, soma e rótulo)
export const VEREDITO: Record<string, [string, 'success' | 'primary' | 'warning' | 'error' | 'default']> = {
  unico: ['Único vendedor', 'success'], testar_alta: ['Testar alta', 'primary'], manter: ['Manter', 'success'],
  nosso_outro_anuncio: ['Já ganhamos com outro anúncio nosso', 'success'],
  enviar_full: ['Enviar ao Full antes de baixar', 'primary'], baixar: ['Baixar ao alvo', 'success'],
  avaliar: ['Avaliar: margem curta', 'warning'], nao_compensa: ['Não compensa: abaixo do piso', 'error'], sem_dado: ['Sem dado de margem', 'default'],
}
export const SITUACAO_CATALOGO: Record<string, [string, 'success' | 'primary' | 'warning' | 'error' | 'default']> = {
  winning: ['Ganhando', 'success'], competing: ['Perdendo', 'error'], sharing_first_place: ['Dividindo o 1º lugar', 'warning'],
  listed: ['Listado', 'default'], not_listed: ['Fora do catálogo', 'default'],
}
export const COBERTURA: Record<Cobertura, [string, 'success' | 'warning' | 'error' | 'default' | 'info']> = {
  ativo: ['no ar', 'success'], vende: ['vendendo', 'info'], pausado: ['pausado', 'warning'], outro: ['outro status', 'default'], falta: ['sem anúncio', 'error'],
  chega: ['chega', 'default'], sem_leitura: ['sem leitura', 'default'],
}
export const SITUACAO_TERCEIRO: [CatalogoTerceiro['situacao'], string][] = [
  ['nao_verificado', 'Não verificado'], ['mesmo_produto', 'Mesmo produto'], ['entrei', 'Entrei'], ['descartado', 'Descartado'],
]

export const nomeLoja = (d: NatalData, k: string) => d.lojas.find((l) => l.store_key === k)?.nome ?? k

/** Anúncios no filtro: loja/canal do topo, categoria/status pelos SKUs filtrados, busca por título, ID ou SKU. */
export function anunciosFiltrados(d: NatalData, f: Filtro, ks: SkuNatal[], busca: string): Anuncio[] {
  const lojas = lojasDoFiltro(d, f), skus = new Set(ks.map((k) => k.sku)), b = busca.trim().toLowerCase()
  return d.anuncios.filter((a) => lojas.includes(a.loja) && a.comps.some(([s]) => skus.has(s)) &&
    (!b || a.titulo.toLowerCase().includes(b) || a.id.toLowerCase().includes(b) || a.comps.some(([s]) => s.toLowerCase().includes(b))))
}

export function resumoAnuncios(as: Anuncio[]) {
  const fat7 = as.reduce((a, x) => a + x.vendas.fat7, 0), mc7 = as.reduce((a, x) => a + x.vendas.mc7, 0)
  return {
    ativos: as.filter((a) => a.status === 'ativo').length, pausados: as.filter((a) => a.status === 'pausado').length,
    soVendas: as.filter((a) => a.fonte === 'vendas').length, fat7, mc7: fat7 ? mc7 / fat7 : null,
    semVenda: as.filter((a) => a.status === 'ativo' && !a.vendas.pecas7 && (a.estoque ?? 0) > 0).length,
    mcBaixa: as.filter((a) => a.preco_sim && !a.preco_sim.erro && a.preco_sim.mc < 0.03).length,
    promo: as.filter((a) => a.preco_de && a.preco && a.preco_de > a.preco).length,
  }
}

/** Fila do dia no filtro de categoria/status (a fila é do SKU, em todos os canais). */
export function filaFiltrada(d: NatalData, ks: SkuNatal[]): ItemFila[] {
  const skus = new Set(ks.map((k) => k.sku))
  return d.fila.filter((x) => skus.has(x.sku))
}

/** Fila em duas partes: SKUs com estoque (ordem do serviço: atrás e à frente primeiro, depois estoque parado a custo)
 * e SKUs aguardando a chegada do lote. */
export function filaHibrida(fila: ItemFila[]): { comEstoque: ItemFila[]; aguardando: ItemFila[] } {
  const tem = (x: ItemFila) => (x.estoque_pecas ?? (x.estoque_custo > 0 ? 1 : 0)) > 0
  return { comEstoque: fila.filter(tem), aguardando: fila.filter((x) => !tem(x)) }
}

/** Anúncio da linha principal: o que mais faturou em 7 dias (o serviço já ordena), preferindo um com MC simulada. */
export function anuncioPrincipal(x: ItemFila): SugestaoPreco | null {
  return x.sugestoes.find((s) => s.mc != null) ?? x.sugestoes[0] ?? null
}

/** Faixa de datas em que o ritmo dos SKUs ainda sem leitura passa a ser julgado. */
export function faixaJulgamento(fila: ItemFila[]): [string, string] | null {
  const ds = fila.filter((x) => x.status === 'SEM_LEITURA' && x.julga_em).map((x) => x.julga_em as string).sort()
  return ds.length ? [ds[0], ds[ds.length - 1]] : null
}

/** Catálogos de terceiros: um por catálogo (o mesmo catálogo pode servir a mais de um SKU). */
export function catalogosTerceiros(d: NatalData, ks: SkuNatal[], soInpiBaixo: boolean, soComVendedor: boolean) {
  const skus = new Set(ks.map((k) => k.sku))
  const m = new Map<string, CatalogoTerceiro & { skus: string[] }>()
  for (const c of d.catalogos_terceiros?.candidatos ?? []) {
    if (!skus.has(c.sku) || (soInpiBaixo && c.risco_inpi !== 'BAIXO') || (soComVendedor && !c.vendedores)) continue
    const g = m.get(c.catalogo_id)
    if (!g) m.set(c.catalogo_id, { ...c, skus: [c.sku] })
    else { g.skus.push(c.sku); if (c.aderencia === 'alta' && g.aderencia !== 'alta') m.set(c.catalogo_id, { ...c, skus: g.skus }) }
  }
  // sem demanda medida (o ML não dá visita/venda de catálogo de terceiro): mesma ordem do briefing (core/briefing.py)
  return [...m.values()].sort((a, b) => (a.aderencia === 'alta' ? 0 : 1) - (b.aderencia === 'alta' ? 0 : 1)
    || (a.vendedores ? 0 : 1) - (b.vendedores ? 0 : 1) || (b.mc_alvo ?? -Infinity) - (a.mc_alvo ?? -Infinity))
}

/** Preço e MC (antes de ads, no preço atual) do anúncio ativo que mais faturou em 7 dias no marketplace. */
export function precoNaLoja(d: NatalData, sku: string, marketplace: string): { preco: number | null; mc: number | null } | null {
  const a = d.anuncios.filter((x) => x.marketplace === marketplace && x.status === 'ativo' && x.comps.length === 1 && x.comps[0][0] === sku)
    .sort((x, y) => y.vendas.fat7 - x.vendas.fat7)[0]
  return a ? { preco: a.preco, mc: a.preco_sim && !a.preco_sim.erro ? a.preco_sim.mc : null } : null
}

/** Motivo do selo "outro status" a partir do status cru do marketplace. */
export function motivoOutro(a: Pick<Anuncio, 'status_api' | 'loja'>): string {
  if (a.status_api === 'under_review') return 'em revisão no ML'
  if (a.status_api === 'fora_do_ar') return 'fora do ar na Shein (a API não diz se foi pausa, aguardando publicação ou esgotado)'
  return a.status_api ? `status ${a.status_api}` : 'outro status'
}

export type CelulaCobertura = { st: Cobertura; anuncios: Anuncio[]; noAr: number; kits: number; motivo: string | null }
export type LinhaCobertura = {
  k: SkuNatal; pecas: number; custoEstoque: number; chegada: string | null; metaRs: number; semCanalRs: number
  cel: Record<string, CelulaCobertura>
}

/** Cobertura por loja: uma linha por SKU com o selo de cada loja, os anúncios por trás dele e a meta sem canal
 * (meta da temporada do SKU a custo × fatia das lojas onde ele não está no ar). Com estoque primeiro, maior R$ em risco antes. */
export function coberturaPorLoja(d: NatalData, ks: SkuNatal[]): { comEstoque: LinhaCobertura[]; aChegar: LinhaCobertura[] } {
  const part = d.premissas.meta_participacao_loja
  const linhas = ks.map((k): LinhaCobertura => {
    const pecas = Object.values(k.estoque).reduce<number>((a, v) => a + (v ?? 0), 0)
    const metaRs = k.qtd_meta * (k.custo || 0)
    const cel: Record<string, CelulaCobertura> = {}
    for (const l of d.lojas) {
      const st = (d.cobertura[k.sku]?.[l.store_key] ?? 'sem_leitura') as Cobertura
      const anuncios = d.anuncios.filter((a) => a.loja === l.store_key && a.comps.some(([s]) => s === k.sku))
      const noAr = anuncios.filter((a) => a.status === 'ativo')
      const outro = anuncios.find((a) => a.status === 'outro')
      cel[l.store_key] = { st, anuncios, noAr: noAr.length, kits: noAr.filter((a) => !(a.comps.length === 1 && a.comps[0][1] === 1)).length,
        motivo: st === 'outro' && outro ? motivoOutro(outro) : null }
    }
    const semCanalRs = d.lojas.reduce((a, l) => a + (['ativo', 'vende'].includes(cel[l.store_key].st) ? 0 : metaRs * (part[l.store_key] ?? 0)), 0)
    const chegada = k.lotes.map((l) => l.data).filter((x) => x > d.hoje).sort()[0] ?? null
    return { k, pecas, custoEstoque: pecas * (k.custo || 0), chegada, metaRs, semCanalRs, cel }
  })
  return {
    comEstoque: linhas.filter((x) => x.pecas > 0).sort((a, b) => b.semCanalRs - a.semCanalRs),
    aChegar: linhas.filter((x) => x.pecas <= 0).sort((a, b) => (a.chegada ?? 'z').localeCompare(b.chegada ?? 'z') || b.semCanalRs - a.semCanalRs),
  }
}

/** Placar por loja: SKUs com estoque no ar e meta (a custo) dos SKUs sem anúncio no ar naquela loja. */
export function placarLojas(d: NatalData, cob: { comEstoque: LinhaCobertura[]; aChegar: LinhaCobertura[] }) {
  const todas = [...cob.comEstoque, ...cob.aChegar]
  return d.lojas.map((l) => ({
    loja: l, meta: d.premissas.meta_participacao_loja[l.store_key] ?? 0,
    noAr: cob.comEstoque.filter((x) => ['ativo', 'vende'].includes(x.cel[l.store_key].st)).length,
    semLeitura: cob.comEstoque.length > 0 && cob.comEstoque.every((x) => ['sem_leitura', 'vende'].includes(x.cel[l.store_key].st)),
    semCanalRs: todas.reduce((a, x) => a + (['ativo', 'vende'].includes(x.cel[l.store_key].st) ? 0 : x.metaRs * (d.premissas.meta_participacao_loja[l.store_key] ?? 0)), 0),
  }))
}

/** Anúncios agrupados pelo SKU do anúncio (kit = SKU próprio; anúncio só nas vendas = componentes). */
export function anunciosPorSku(as: Anuncio[]): { chave: string; anuncios: Anuncio[] }[] {
  const m = new Map<string, Anuncio[]>()
  for (const a of as) {
    const chave = a.sku_anuncio ?? a.comps.map((c) => c[0]).join(' + ')
    m.set(chave, [...(m.get(chave) ?? []), a])
  }
  return [...m.entries()].map(([chave, anuncios]) => ({ chave, anuncios }))
}


// ------------------------------------------------------------------ Vendas por anúncio (Visão geral, 14/10/2026)
export type FiltroVendas = { de: string; ate: string; titulo: string; skus: string[]; lojas: string[]; semVenda: boolean }
export type Somas = { pedidos: number; un: number; pecas: number; fat: number; mc: number; pend: number }
export type LinhaVendaAnuncio = {
  chave: string; loja: string; anuncio: string; variacao: string | null
  titulo: string; imagem: string | null; link: string | null; nomeVariacao: string | null; id: string
  comps: [string, number][]; status: Anuncio['status'] | 'nao_identificada'; full: boolean | null
  preco: number | null; estoque: number | null; visitas: number | null
  atual: Somas; anterior: Somas | null; conversao: number | null
}
const zero = (): Somas => ({ pedidos: 0, un: 0, pecas: 0, fat: 0, mc: 0, pend: 0 })

/** Uma linha por anúncio × variação: nunca junta anúncios nem variações (pedido com vários anúncios já vem separado
 * pelo serviço). Kit = um anúncio = uma linha; pedidos e unidades do kit = o maior entre os seus SKUs (o mesmo pedido
 * aparece em cada SKU do kit), peças/faturamento/MC = a soma. */
export function vendasPorVariacao(d: NatalData, f: FiltroVendas): LinhaVendaAnuncio[] {
  const cal = d.calendario
  const iDe = cal.indexOf(f.de), iAte = cal.indexOf(f.ate)
  if (iDe < 0 || iAte < iDe) return []
  const n = iAte - iDe + 1, pDe = iDe - n, pAte = iDe - 1
  // (loja|anúncio|variação|sku) → somas no período e no anterior
  const acc = new Map<string, { cur: Somas; ant: Somas }>()
  for (const [lj, ad, mo, sku, i, ped, un, pc, fat, mc, pend] of d.vendas_dia_anuncio ?? []) {
    const dentro = i >= iDe && i <= iAte, antes = pDe >= 0 && i >= pDe && i <= pAte
    if (!dentro && !antes) continue
    const k = `${lj}|${ad}|${mo ?? ''}|${sku}`
    const a = acc.get(k) ?? { cur: zero(), ant: zero() }
    const alvo = dentro ? a.cur : a.ant
    alvo.pedidos += ped; alvo.un += un; alvo.pecas += pc; alvo.fat += fat; alvo.mc += mc; alvo.pend += pend
    acc.set(k, a)
  }
  const usadas = new Set<string>()
  const somar = (chaves: string[], qual: 'cur' | 'ant') => {
    const t = zero()
    for (const k of chaves) {
      const x = acc.get(k)?.[qual]
      if (!x) continue
      t.pedidos = Math.max(t.pedidos, x.pedidos); t.un = Math.max(t.un, x.un)
      t.pecas += x.pecas; t.fat += x.fat; t.mc += x.mc; t.pend += x.pend
    }
    return t
  }
  const ih = d.i_hoje, semana = ih >= 6 && iAte === ih && iDe === ih - 6
  const linhas: LinhaVendaAnuncio[] = []
  const vistos = new Set<string>()
  for (const a of d.anuncios) {
    const item = a.item_id ?? a.id
    const chave = `${a.loja}|${a.id}`
    if (vistos.has(chave)) continue
    vistos.add(chave)
    const mo = a.model_id ?? null
    const chaves = a.comps.flatMap(([s]) => [`${a.loja}|${item}||${s}`, ...(mo ? [`${a.loja}|${item}|${mo}|${s}`] : [])])
    chaves.forEach((k) => usadas.add(k))
    const atual = somar(chaves, 'cur')
    linhas.push({
      chave, loja: a.loja, anuncio: item, variacao: mo, titulo: a.titulo, imagem: a.imagem, link: a.link, nomeVariacao: a.variacao ?? null, id: item,
      comps: a.comps, status: a.status, full: a.full, preco: a.preco, estoque: a.estoque, visitas: a.visitas_7d,
      atual, anterior: pDe >= 0 ? somar(chaves, 'ant') : null,
      conversao: semana && a.visitas_7d ? atual.un / a.visitas_7d : null,
    })
  }
  // venda que não casou com nenhuma variação da foto (ex.: "?" = duas variações com o mesmo SKU sem como separar)
  const sobras = new Map<string, string[]>()
  for (const k of acc.keys()) {
    if (usadas.has(k)) continue
    const [lj, ad, mo] = k.split('|')
    const g = `${lj}|${ad}|${mo}`
    sobras.set(g, [...(sobras.get(g) ?? []), k])
  }
  for (const [g, chaves] of sobras) {
    const [lj, ad, mo] = g.split('|')
    const ref = d.anuncios.find((a) => a.loja === lj && (a.item_id ?? a.id) === ad)
    linhas.push({
      chave: `${g}|sobra`, loja: lj, anuncio: ad, variacao: mo || null, titulo: ref?.titulo ?? `Anúncio ${ad}`, imagem: ref?.imagem ?? null,
      link: ref?.link ?? null, nomeVariacao: mo === '?' ? 'variação não identificada' : mo || null, id: ad,
      comps: chaves.map((k) => [k.split('|')[3], 1] as [string, number]), status: 'nao_identificada', full: null, preco: null, estoque: null,
      visitas: null, atual: somar(chaves, 'cur'), anterior: pDe >= 0 ? somar(chaves, 'ant') : null, conversao: null,
    })
  }
  const txt = f.titulo.trim().toLowerCase()
  return linhas.filter((l) => (!f.lojas.length || f.lojas.includes(l.loja))
    && (!f.skus.length || l.comps.some(([s]) => f.skus.includes(s)))
    && (!txt || `${l.titulo} ${l.id} ${l.nomeVariacao ?? ''} ${l.comps.map(([s]) => s).join(' ')}`.toLowerCase().includes(txt))
    && (f.semVenda || l.atual.fat > 0 || l.atual.un > 0))
}

export function totalVendas(ls: LinhaVendaAnuncio[]): { atual: Somas; anterior: Somas | null } {
  const soma = (xs: Somas[]) => xs.reduce((t, x) => ({ pedidos: t.pedidos + x.pedidos, un: t.un + x.un, pecas: t.pecas + x.pecas, fat: t.fat + x.fat, mc: t.mc + x.mc, pend: t.pend + x.pend }), zero())
  return { atual: soma(ls.map((l) => l.atual)), anterior: ls.length && ls.every((l) => l.anterior) ? soma(ls.map((l) => l.anterior as Somas)) : null }
}
